import {
  Flex,
  Group,
  NumberFormatter,
  Paper,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';

import { type ReactNode } from 'react';

import { StatusRow } from './StatusRow';

import { safeCastNumber } from 'utils/numberUtils';
import { useProviderDashboardPalette } from './useProviderDashboardPalette';

interface StreamCardProps {
  title: string;
  subtitle: string;
  accent: string;
  icon: ReactNode;
  total: number;
  rangeLabel: string;
  isLoading?: boolean;
  dateBasis?: string;
  rows: Array<{
    label: string;
    value: number;
    onClick?: () => void;
    color: string;
    tooltip?: string;
  }>;
}

export const StreamCard = ({
  title,
  subtitle,
  accent,
  icon,
  total,
  rangeLabel,
  isLoading = false,
  rows,
  dateBasis,
}: StreamCardProps) => {
  const { translate } = useTranslation();
  const palette = useProviderDashboardPalette();
  const enumToTitleCase = (str: string) => {
    return str
      .replace('billed', 'payment')
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const resolvedDateBasis = dateBasis || 'billed-date';
  return (
    <Paper
      p="lg"
      radius="lg"
      style={{
        background: palette.cardBackground,
        border: `1px solid ${accent}55`,
        minHeight: '100%',
      }}
    >
      <Stack gap="lg">
        <Flex justify="space-between" align="flex-start" gap="sm">
          <Group gap="sm" align="flex-start">
            <ThemeIcon
              size={34}
              radius="md"
              variant="filled"
              style={{ background: `${accent}22`, color: accent }}
            >
              {icon}
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={700} size="lg" c={palette.primaryText}>
                {title}
              </Text>
              <Text size="sm" c={palette.subtleText}>
                {subtitle}
              </Text>
            </Stack>
          </Group>
        </Flex>

        {isLoading ? (
          <Stack gap="md" data-testid="provider-stream-card-skeleton">
            <Skeleton height={84} radius="lg" />
            <Skeleton height={18} />
            <Skeleton height={18} />
            <Skeleton height={18} />
            <Skeleton height={18} />
          </Stack>
        ) : (
          <>
            <Paper
              radius="lg"
              p="md"
              style={{
                background: `${accent}10`,
                border: `1px solid ${accent}33`,
              }}
            >
              <Stack gap={8}>
                <Text size="sm" c={palette.subtleText}>
                  {translate(
                    'pages:providerDashboard.metrics.streamCard.totalBy',
                    {
                      basis: enumToTitleCase(resolvedDateBasis),
                    }
                  )}
                </Text>
                <NumberFormatter
                  prefix="$"
                  value={safeCastNumber(total)}
                  thousandSeparator
                  decimalScale={2}
                  fixedDecimalScale
                  style={{
                    fontSize: 20,
                    fontWeight: 800,
                    color: palette.primaryText,
                  }}
                />
                <Text size="sm" fw={600} c={accent}>
                  {rangeLabel}
                </Text>
              </Stack>
            </Paper>

            <Stack gap={0}>
              <Text size="sm" c={palette.subtleText} mb="xs">
                {translate(
                  'pages:providerDashboard.metrics.streamCard.statusBreakdown',
                  'Status Breakdown'
                )}
              </Text>
              {rows.map((row) => (
                <StatusRow
                  key={row.label}
                  label={row.label}
                  value={row.value}
                  onClick={row.onClick}
                  color={row.color}
                  tooltip={row.tooltip}
                />
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};
